import { useMemo } from "react"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import {
  faBan,
  faCalendarCheck,
  faCalendarDays,
  faChartLine,
  faClose,
  faClock,
  faLocationDot,
  faPeopleGroup,
  faStar,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { MetricCard } from "../../../components/MetricCard/MetricCard"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { RestaurantReservation } from "../../../Objects/RestaurantReservation/RestaurantReservationTypes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { escapeHtml, openPrintReport } from "../../../utils/printReport"

interface ReservationMetricsPopupProps {
  reservations: RestaurantReservation[]
  startDate: string
  endDate: string
  onClose: () => void
}

type MetricsRow = {
  key: string
  label: string
  count: number
  partySize: number
}

type SummaryCard = {
  key: string
  value: string
  subtitle?: string
  icon: IconProp
  color?: string
}

const parseDate = (value: string | Date) => {
  if (value instanceof Date) return value

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number)

    return new Date(year, month - 1, day)
  }

  return new Date(value)
}

const formatDate = (value: string | Date) => parseDate(value).toLocaleDateString("pt-BR")

const formatTime = (value: string | Date) =>
  parseDate(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })

const increment = (map: Map<string, MetricsRow>, key: string, label: string, partySize: number) => {
  const current = map.get(key)

  if (current) {
    current.count += 1
    current.partySize += partySize
    return
  }

  map.set(key, { key, label, count: 1, partySize })
}

const sortRows = (rows: MetricsRow[]) => rows.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))

export const ReservationMetricsPopup: React.FC<ReservationMetricsPopupProps> = ({
  reservations,
  startDate,
  endDate,
  onClose,
}) => {
  const { t } = useTranslation("restaurantReservations")

  const metrics = useMemo(() => {
    const confirmed = reservations.filter((reservation) => reservation.status === "Confirmed")
    
    const cancelled = reservations.filter((reservation) => reservation.status === "Cancelled")
    
    const byTime = new Map<string, MetricsRow>()
    
    const byArea = new Map<string, MetricsRow>()
   
    const byDate = new Map<string, MetricsRow>()

    confirmed.forEach((reservation) => {
      const startsAt = new Date(reservation.startsAt)
      const time = formatTime(startsAt)
      const date = formatDate(startsAt)

      increment(byTime, time, time, reservation.partySize)
      increment(byArea, reservation.areaName, reservation.areaName, reservation.partySize)
      increment(byDate, date, date, reservation.partySize)
    })

    const sortedReservations = [...confirmed].sort(
      (a, b) =>
        new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime() ||
        a.areaName.localeCompare(b.areaName) ||
        a.customerName.localeCompare(b.customerName),
    )

    return {
      total: reservations.length,
      confirmed: confirmed.length,
      cancelled: cancelled.length,
      partySize: confirmed.reduce((total, reservation) => total + reservation.partySize, 0),
      customers: new Set(confirmed.map((reservation) => reservation.customerId)).size,
      byTime: sortRows([...byTime.values()]),
      byArea: sortRows([...byArea.values()]),
      byDate: sortRows([...byDate.values()]),
      printReservations: sortedReservations,
    }
  }, [reservations])

  const averagePartySize = metrics.confirmed > 0 ? metrics.partySize / metrics.confirmed : 0

  const cancellationRate = metrics.total > 0 ? (metrics.cancelled / metrics.total) * 100 : 0

  const peakTime = metrics.byTime[0]

  const peakArea = metrics.byArea[0]

  const peakDate = metrics.byDate[0]

  const summaryCards: SummaryCard[] = [
    {
      key: "confirmed",
      value: String(metrics.confirmed),
      subtitle: t("metrics.total_with_count", { count: metrics.total }),
      icon: faCalendarCheck,
      color: "#059669",
    },
    {
      key: "people",
      value: String(metrics.partySize),
      subtitle: t("metrics.average_party_value", {
        value: averagePartySize.toLocaleString("pt-BR", { maximumFractionDigits: 1 }),
      }),
      icon: faPeopleGroup,
      color: "#ea580c",
    },
    {
      key: "customers",
      value: String(metrics.customers),
      subtitle: t("metrics.unique_customers"),
      icon: faUserCheck,
      color: "#2563eb",
    },
    {
      key: "cancelled",
      value: String(metrics.cancelled),
      subtitle: t("metrics.cancellation_rate_value", {
        value: cancellationRate.toLocaleString("pt-BR", { maximumFractionDigits: 1 }),
      }),
      icon: faBan,
      color: "#dc2626",
    },
  ]

  const renderDistribution = (title: string, icon: IconProp, rows: MetricsRow[], empty: string) => {
    const maxCount = Math.max(...rows.map((row) => row.count), 1)

    return (
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <section className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 shadow-sm">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />

          <div className="flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-slate-700">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500 shadow-sm">
                <FontAwesomeIcon icon={icon} />
              </span>
              {title}
            </h3>
          </div>

          {rows.length === 0 ? (
            <Animate type={AnimationTypes.fadeInTraslate} duration="0.7s">
              <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-600">
                {empty}
              </p>
            </Animate>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {rows.slice(0, 8).map((row, index) => (
                <Animate key={row.key} type={AnimationTypes.fadeInTraslate} duration={`${0.7 + index * 0.08}s`}>
                  <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-slate-900">{row.label}</p>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          {t("metrics.people_count", { count: row.partySize })}
                        </p>
                      </div>

                      <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-black text-orange-700">
                        {t("metrics.reservation_count", { count: row.count })}
                      </span>
                    </div>

                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-orange-400 to-amber-400"
                        style={{ width: `${Math.max((row.count / maxCount) * 100, 8)}%` }}
                      />
                    </div>
                  </div>
                </Animate>
              ))}
            </div>
          )}
        </section>
      </Animate>
    )
  }

  const handlePrint = () => {
    const grouped = new Map<string, RestaurantReservation[]>()

    metrics.printReservations.forEach((reservation) => {
      const startsAt = new Date(reservation.startsAt)
      const key = `${formatDate(startsAt)} ${formatTime(startsAt)}`
      const list = grouped.get(key) ?? []

      list.push(reservation)
      grouped.set(key, list)
    })

    const sections = [...grouped.entries()]
      .map(([group, groupReservations]) => {
        const headers = [t("fields.area"), t("metrics.customer"), t("fields.party_size"), t("fields.notes")]
        const rows = groupReservations
          .map((reservation) => {
            const cells = [
              reservation.areaName,
              reservation.customerName,
              String(reservation.partySize),
              reservation.notes ?? "",
            ]

            return `<tr>${cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`
          })
          .join("")

        return `<section><h2>${escapeHtml(group)}</h2><table><thead><tr>${headers
          .map((header) => `<th>${escapeHtml(header)}</th>`)
          .join("")}</tr></thead><tbody>${rows}</tbody></table></section>`
      })
      .join("")

    const period = t("metrics.print_period", { startDate: formatDate(startDate), endDate: formatDate(endDate) })

    openPrintReport({
      title: t("metrics.print_title"),
      subtitle: period,
      bodyHtml: sections || `<div class="empty">${escapeHtml(t("metrics.empty"))}</div>`,
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("metrics.title")}
              icon={faChartLine}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:restaurantReservations.metricsPopup.close"
                />
              }
            />

            <div className="p-2">
              <Animate type={AnimationTypes.fadeInTraslate} duration="0.8s">
                <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-r from-white via-orange-50 to-orange-100 p-4 shadow-sm">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />

                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className="mb-2 inline-flex items-center gap-2 rounded-lg bg-orange-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-orange-600">
                        <FontAwesomeIcon icon={faChartLine} />
                        {t("metrics.panel_label")}
                      </div>

                      <p className="max-w-2xl text-sm font-semibold leading-6 text-slate-600">
                        {t("metrics.subtitle")}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-xl border-2 border-orange-200 bg-white/90 px-3 py-2 text-xs font-bold text-slate-600 shadow-sm">
                        {t("metrics.print_period", { startDate: formatDate(startDate), endDate: formatDate(endDate) })}
                      </span>
                    </div>
                  </div>
                </div>
              </Animate>

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                {summaryCards.map((card) => (
                  <MetricCard
                    key={card.key}
                    title={t(`metrics.${card.key}`)}
                    value={card.value}
                    subtitle={card.subtitle}
                    icon={card.icon}
                    color={card.color}
                  />
                ))}
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                <Animate type={AnimationTypes.fadeInTraslate} duration="0.9s">
                  <div className="rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-white to-emerald-50 p-4 shadow-sm">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700">
                      <FontAwesomeIcon icon={faStar} />
                      {t("metrics.peak_time")}
                    </p>
                    <strong className="mt-2 block text-xl font-black text-slate-900">
                      {peakTime?.label ?? t("metrics.no_data")}
                    </strong>
                    {peakTime && (
                      <span className="mt-1 block text-xs font-bold text-slate-500">
                        {t("metrics.reservation_count", { count: peakTime.count })}
                      </span>
                    )}
                  </div>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
                  <div className="rounded-xl border-2 border-sky-200 bg-gradient-to-br from-white to-sky-50 p-4 shadow-sm">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-sky-700">
                      <FontAwesomeIcon icon={faLocationDot} />
                      {t("metrics.peak_area")}
                    </p>
                    <strong className="mt-2 block truncate text-xl font-black text-slate-900">
                      {peakArea?.label ?? t("metrics.no_data")}
                    </strong>
                    {peakArea && (
                      <span className="mt-1 block text-xs font-bold text-slate-500">
                        {t("metrics.people_count", { count: peakArea.partySize })}
                      </span>
                    )}
                  </div>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
                  <div className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-white to-violet-50 p-4 shadow-sm">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-violet-700">
                      <FontAwesomeIcon icon={faCalendarDays} />
                      {t("metrics.peak_date")}
                    </p>
                    <strong className="mt-2 block text-xl font-black text-slate-900">
                      {peakDate?.label ?? t("metrics.no_data")}
                    </strong>
                    {peakDate && (
                      <span className="mt-1 block text-xs font-bold text-slate-500">
                        {t("metrics.reservation_count", { count: peakDate.count })}
                      </span>
                    )}
                  </div>
                </Animate>
              </div>

              <div className="mt-3 grid gap-3 lg:grid-cols-3">
                {renderDistribution(t("metrics.by_time"), faClock, metrics.byTime, t("metrics.empty"))}
                {renderDistribution(t("metrics.by_area"), faLocationDot, metrics.byArea, t("metrics.empty"))}
                {renderDistribution(t("metrics.by_date"), faCalendarDays, metrics.byDate, t("metrics.empty"))}
              </div>

              <div className={defaultStyle.divisionBar} />

              <div className={`${defaultStyle.buttonsController} pb-2`}>
                <Button
                  label={t("actions.print_reservations")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  disabled={metrics.printReservations.length === 0}
                  onClick={handlePrint}
                  descriptionKey="interactionDescriptions:restaurantReservations.metricsPopup.print"
                  ariaLabel={t("actions.print_reservations")}
                />
              </div>
            </div>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
